/**
 * The header's "Ações" menu beside ▶ Executar (one button, so the bar keeps its size):
 *
 * - **Analisar** sends the catalog's "run.analyze": the agent checks the tests, the security and the
 *   quality against the audits' own rules, changes nothing, and answers with suggestions and a fix
 *   prompt — shown here, editable, one click from being sent.
 * - **O que falta** asks two plain questions (an app in a store? a domain?), sends "run.launch", and
 *   shows how ready the project is, whether there is an MVP and the next stage to production. A
 *   project begun with Iniciar projeto already answered them in its interview (lib/startProject.ts):
 *   those answers are used, and only what is still unknown is asked.
 * - **Pendências** opens the project's pending list (lib/pendingList.ts).
 *
 * Like ▶, each follows its run (lib/repositoryRun.ts): an hourglass while it waits, ■ while an agent
 * works, and the result when it ends. The prompts come from the server, so their rules are edited on
 * the Prompts screen, not here.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, ClipboardList, Copy, Gauge, Hourglass, ListChecks, Loader2, Rocket, Send, Square } from 'lucide-react'
import { api } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { localized, usePromptCatalog } from '../lib/prompts'
import { analysisOf, readinessOf, useRepositoryRun, type LaunchStage, type RunPhase } from '../lib/repositoryRun'
import { ensurePendingList } from '../lib/pendingList'
import { interviewDocPath, launchAnswers } from '../lib/startProject'
import { stopRunnerFor } from '../lib/runnerHost'
import type { CatalogPrompt, RepositoryLink, WorkOrder } from '../lib/types'
import { Modal } from './Modal'
import { Markdown } from './Markdown'
import { Dropdown, DropdownItem } from './Dropdown'
import { PluginButtons } from './PluginButtons'
import { PROJECT_ACTION_EVENT } from '../lib/composer'
import { useWhoDoesIt } from './WhoDoesIt'
import type { WorkTarget } from '../lib/prompts'

/** What the project's interview already answered for "O que falta"; null where it is still to ask. */
type Known = { store: string | null; domain: string | null }

type Shown = { kind: 'analysis' | 'launch'; order: WorkOrder } | { kind: 'questions'; known: Known } | null

/** `only`: Kids mode (docs/refactor/48) keeps Analisar alone. */
export function ProjectActions({ repository, only }: { repository: RepositoryLink; only?: 'analyze' }) {
  const { t, locale } = useI18n()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: catalog } = usePromptCatalog()
  const [shown, setShown] = useState<Shown>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const analyzePrompt = catalog?.prompts.find((p) => p.key === 'run.analyze')
  const launchPrompt = catalog?.prompts.find((p) => p.key === 'run.launch')

  const analysis = useRepositoryRun(repository.id, useCallback((order: WorkOrder) => setShown({ kind: 'analysis', order }), []), 'analyze')
  const launch = useRepositoryRun(repository.id, useCallback((order: WorkOrder) => setShown({ kind: 'launch', order }), []), 'launch')

  const who = useWhoDoesIt({ id: repository.id, name: repository.name })
  const begin = useMutation({
    mutationFn: async ({ prompt, body, kind, target }: { prompt: CatalogPrompt; body: string; kind: 'analyze' | 'launch'; target: WorkTarget }) => {
      const order = await api.workOrders.create({ kind: prompt.kind, instructions: body, title: localized(prompt.title, locale), repositoryId: repository.id, maxIterations: null, ...target })
      ;(kind === 'analyze' ? analysis : launch).follow(order.id)
    },
    onError: () => setNotice(t('run.failed')),
  })
  // "Quem faz?": alone with one agent it starts at once; otherwise the person picks who (components/WhoDoesIt.tsx).
  const { ask } = who
  const { isPending, mutate: beginMutate } = begin
  const start = useMemo(
    () => ({
      isPending,
      mutate: (work: { prompt: CatalogPrompt; body: string; kind: 'analyze' | 'launch' }) => void ask((target) => beginMutate({ ...work, target })),
    }),
    [isPending, ask, beginMutate],
  )
  const stop = useMutation({
    mutationFn: async (order: WorkOrder | null) => {
      stopRunnerFor(repository.name)
      if (order) await api.workOrders.stop(order.id)
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['run-order'] }),
  })
  // "O que falta": what the project's interview answered is not asked again.
  const askLaunch = useMutation({
    mutationFn: async (): Promise<Known> => {
      try {
        return launchAnswers((await api.docs.byPath(interviewDocPath(repository.name))).content)
      } catch {
        return { store: null, domain: null }
      }
    },
    onSuccess: (known) => {
      if (known.store && known.domain && launchPrompt) {
        start.mutate({ prompt: launchPrompt, body: launchPrompt.body.replace('{{store}}', known.store).replace('{{domain}}', known.domain), kind: 'launch' })
      } else setShown({ kind: 'questions', known })
    },
  })
  const pending = useMutation({
    mutationFn: () => ensurePendingList(repository.name),
    onSuccess: (path) => {
      void queryClient.invalidateQueries({ queryKey: ['docs'] })
      navigate(`/project/docs?path=${encodeURIComponent(path)}`)
    },
    onError: () => setNotice(t('project.pendingFailed')),
  })

  // /analisar, /o-que-falta and /pendencias in the prompt box: the same as choosing them here.
  useEffect(() => {
    const onAction = (event: Event) => {
      const name = (event as CustomEvent<string>).detail
      if (name === 'analisar' && analyzePrompt && analysis.phase === 'idle') start.mutate({ prompt: analyzePrompt, body: analyzePrompt.body, kind: 'analyze' })
      // As in the menu: what the project's interview answered is not asked again.
      if (name === 'o-que-falta' && launchPrompt && launch.phase === 'idle') askLaunch.mutate()
      if (name === 'pendencias') pending.mutate()
    }
    window.addEventListener(PROJECT_ACTION_EVENT, onAction)
    return () => window.removeEventListener(PROJECT_ACTION_EVENT, onAction)
  }, [analyzePrompt, launchPrompt, analysis.phase, launch.phase, start, pending, askLaunch])

  // The menu's own button tells what is going on inside it: the action running, waiting or working.
  const running = analysis.phase !== 'idle' ? { phase: analysis.phase, label: t('project.analyze') }
    : launch.phase !== 'idle' ? { phase: launch.phase, label: t('project.launch') }
      : null
  const side = (phase: RunPhase) => (phase === 'working' ? t('project.menu.working') : phase === 'waiting' ? t('project.menu.waiting') : undefined)

  return (
    <>
      <Dropdown
        label={running ? `${t('project.menu')} — ${running.label}` : t('project.menu')}
        buttonClassName={`btn btn--sm run-bar__aux run-bar__actions${running?.phase === 'waiting' ? ' is-waiting' : ''}${running?.phase === 'working' ? ' is-working' : ''}`}
        button={
          <>
            {running?.phase === 'working' ? <Loader2 size={14} className="spin" /> : running?.phase === 'waiting' ? <Hourglass size={14} className="run-bar__hourglass" /> : <ListChecks size={14} />}
            <span>{running ? running.label : t('project.menu')}</span>
            <ChevronDown size={13} aria-hidden />
          </>
        }
      >
        {(close) => (
          <>
            {analyzePrompt && (
              <DropdownItem
                icon={analysis.phase === 'working' ? <Square size={12} fill="currentColor" /> : <Gauge size={14} />}
                label={analysis.phase === 'idle' ? t('project.analyze') : `${t('run.stop')} · ${t('project.analyze')}`}
                hint={analysis.phase === 'idle' ? t('project.analyzeHint') : t('run.stopHint')}
                side={side(analysis.phase)}
                disabled={start.isPending}
                onSelect={() => {
                  close()
                  if (analysis.phase === 'idle') start.mutate({ prompt: analyzePrompt, body: analyzePrompt.body, kind: 'analyze' })
                  else stop.mutate(analysis.order)
                }}
              />
            )}
            {launchPrompt && !only && (
              <DropdownItem
                icon={launch.phase === 'working' ? <Square size={12} fill="currentColor" /> : <Rocket size={14} />}
                label={launch.phase === 'idle' ? t('project.launch') : `${t('run.stop')} · ${t('project.launch')}`}
                hint={launch.phase === 'idle' ? t('project.launchHint') : t('run.stopHint')}
                side={side(launch.phase)}
                disabled={start.isPending || askLaunch.isPending}
                onSelect={() => {
                  close()
                  if (launch.phase === 'idle') askLaunch.mutate()
                  else stop.mutate(launch.order)
                }}
              />
            )}
            {!only && <DropdownItem
              icon={pending.isPending ? <Loader2 size={14} className="spin" /> : <ClipboardList size={14} />}
              label={t('project.pending')}
              hint={t('project.pendingHint')}
              disabled={pending.isPending}
              onSelect={() => {
                close()
                pending.mutate()
              }}
            />}
            {/* What installed plugins add to the header lives here too, so the bar keeps its size. */}
            {!only && (
              <div className="dropdown__plugins">
                <PluginButtons placement="header" />
              </div>
            )}
          </>
        )}
      </Dropdown>
      {who.dialog}
      {notice && <span className="tiny run-bar__notice run-bar__notice--bad" role="status">{notice}</span>}

      {shown?.kind === 'questions' && launchPrompt && (
        <LaunchQuestions
          known={shown.known}
          onClose={() => setShown(null)}
          onStart={(store, domain) => {
            setShown(null)
            const body = launchPrompt.body.replace('{{store}}', store).replace('{{domain}}', domain)
            start.mutate({ prompt: launchPrompt, body, kind: 'launch' })
          }}
        />
      )}
      {shown?.kind === 'analysis' && (
        <AnalysisResult order={shown.order} repository={repository} onClose={() => setShown(null)} onPending={() => pending.mutate()} />
      )}
      {shown?.kind === 'launch' && <LaunchResult order={shown.order} onClose={() => setShown(null)} onPending={() => pending.mutate()} />}
    </>
  )
}

const STORE_CHOICES = ['none', 'notYet', 'play', 'appStore', 'both'] as const
type StoreChoice = (typeof STORE_CHOICES)[number]

/** "O que falta": what the agent cannot find out alone, asked before it starts. */
function LaunchQuestions({ known, onClose, onStart }: { known: Known; onClose: () => void; onStart: (store: string, domain: string) => void }) {
  const { t } = useI18n()
  const [store, setStore] = useState<StoreChoice>('none')
  const [hasDomain, setHasDomain] = useState<'no' | 'yes'>('no')
  const [domain, setDomain] = useState('')
  const storeAnswer: Record<StoreChoice, string> = {
    none: 'there is no mobile app in this solution',
    notYet: 'there is a mobile app, not published in any store yet',
    play: 'the app is published on Google Play only',
    appStore: 'the app is published on the Apple App Store only',
    both: 'the app is published on Google Play and on the Apple App Store',
  }
  const domainAnswer = known.domain ?? (hasDomain === 'yes' && domain.trim() ? `they have the domain ${domain.trim().slice(0, 120)}` : 'they do not have a domain yet')
  return (
    <Modal title={t('project.launchTitle')} onClose={onClose}>
      <div className="stack">
        <p className="small muted" style={{ margin: 0 }}>{t('project.launchIntro')}</p>
        {(known.store || known.domain) && <p className="tiny muted" style={{ margin: 0 }}>{t('project.fromInterview')}</p>}
        {!known.store && <fieldset className="stack launch-q">
          <legend className="small"><strong>{t('project.qStore')}</strong></legend>
          {STORE_CHOICES.map((choice) => (
            <label key={choice} className="row row--tight small">
              <input type="radio" name="store" checked={store === choice} onChange={() => setStore(choice)} />
              {t(`project.store.${choice}`)}
            </label>
          ))}
        </fieldset>}
        {!known.domain && <fieldset className="stack launch-q">
          <legend className="small"><strong>{t('project.qDomain')}</strong></legend>
          <label className="row row--tight small">
            <input type="radio" name="domain" checked={hasDomain === 'no'} onChange={() => setHasDomain('no')} /> {t('project.domainNo')}
          </label>
          <label className="row row--tight small">
            <input type="radio" name="domain" checked={hasDomain === 'yes'} onChange={() => setHasDomain('yes')} /> {t('project.domainYes')}
          </label>
          {hasDomain === 'yes' && (
            <input className="input" placeholder="meusite.com.br" value={domain} onChange={(event) => setDomain(event.target.value)} aria-label={t('project.domainYes')} />
          )}
        </fieldset>}
        <div className="row row--between">
          <button type="button" className="btn btn--ghost" onClick={onClose}>{t('common.cancel')}</button>
          <button type="button" className="btn btn--primary" onClick={() => onStart(known.store ?? storeAnswer[store], domainAnswer)}>
            <Rocket size={14} /> {t('project.launchStart')}
          </button>
        </div>
      </div>
    </Modal>
  )
}

/** The analysis, its suggestions, and the fix prompt — editable, and sent with one click. */
function AnalysisResult({ order, repository, onClose, onPending }: { order: WorkOrder; repository: RepositoryLink; onClose: () => void; onPending: () => void }) {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const { report, fixPrompt } = analysisOf(order.lastReply ?? order.lastReport ?? '')
  const [fix, setFix] = useState(fixPrompt ?? '')
  const [sent, setSent] = useState(false)
  const send = useMutation({
    mutationFn: () => api.workOrders.create({ kind: 'Once', instructions: fix.trim(), title: t('project.fixTitle'), repositoryId: repository.id, maxIterations: null }),
    onSuccess: () => {
      setSent(true)
      void queryClient.invalidateQueries({ queryKey: ['work-orders'] })
    },
  })
  return (
    <Modal title={t('project.analyzeTitle')} onClose={onClose} wide>
      <div className="stack project-result">
        {order.status !== 'Done' && <p className="small run-bar__notice--bad" style={{ margin: 0 }}>{order.status === 'Stopped' ? t('run.stopped') : t('run.failedRun')}</p>}
        {report ? <div className="project-result__report"><Markdown source={report} /></div> : <p className="small muted">{t('project.noReport')}</p>}
        {fixPrompt !== null && (
          <section className="stack" style={{ gap: 6 }}>
            <strong className="small">{t('project.fixPrompt')}</strong>
            <p className="tiny muted" style={{ margin: 0 }}>{t('project.fixPromptHint')}</p>
            <textarea className="input mono project-result__fix" rows={8} value={fix} onChange={(event) => setFix(event.target.value)} aria-label={t('project.fixPrompt')} />
          </section>
        )}
        {sent && <p className="small" role="status" style={{ margin: 0, color: 'var(--ok-text)' }}>{t('project.fixSent')}</p>}
        <div className="row row--between row--wrap">
          <button type="button" className="btn btn--ghost" onClick={onPending}><ClipboardList size={14} /> {t('project.pending')}</button>
          <div className="row row--tight">
            {fixPrompt !== null && (
              <button type="button" className="btn btn--ghost" onClick={() => void navigator.clipboard?.writeText(fix)}>
                <Copy size={14} /> {t('common.copy')}
              </button>
            )}
            {fixPrompt !== null && (
              <button type="button" className="btn btn--primary" onClick={() => send.mutate()} disabled={!fix.trim() || send.isPending || sent}>
                {send.isPending ? <Loader2 size={14} className="spin" /> : <Send size={14} />} {t('project.fixSend')}
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}

/** How ready the project is, whether there is an MVP, and the next stage. */
function LaunchResult({ order, onClose, onPending }: { order: WorkOrder; onClose: () => void; onPending: () => void }) {
  const { t } = useI18n()
  const { ready, mvp, stage, body } = readinessOf(order.lastReply ?? order.lastReport ?? '')
  return (
    <Modal title={t('project.launchTitle')} onClose={onClose} wide>
      <div className="stack project-result">
        {order.status !== 'Done' && <p className="small run-bar__notice--bad" style={{ margin: 0 }}>{order.status === 'Stopped' ? t('run.stopped') : t('run.failedRun')}</p>}
        {ready !== null && (
          <div className="readiness">
            <div className="readiness__value">{ready}%</div>
            <div className="stack" style={{ gap: 6, flex: 1 }}>
              <div className="readiness__bar" role="progressbar" aria-valuenow={ready} aria-valuemin={0} aria-valuemax={100}>
                <span style={{ width: `${ready}%` }} />
              </div>
              <div className="row row--tight row--wrap small">
                {mvp !== null && <span className={`readiness__chip${mvp ? ' is-ok' : ''}`}>{mvp ? t('project.mvpYes') : t('project.mvpNo')}</span>}
                {stage && <span className="readiness__chip is-next">{t('project.next')}: {t(`project.stage.${stage as LaunchStage}`)}</span>}
              </div>
            </div>
          </div>
        )}
        {body ? <div className="project-result__report"><Markdown source={body} /></div> : <p className="small muted">{t('project.noReport')}</p>}
        <div className="row row--between">
          <button type="button" className="btn btn--primary" onClick={onPending}><ClipboardList size={14} /> {t('project.pendingOpen')}</button>
          <button type="button" className="btn btn--ghost" onClick={onClose}>{t('common.close')}</button>
        </div>
      </div>
    </Modal>
  )
}
